var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns";
import { ReportParamType, ReportRunStatus } from "healthex-common/reports.dtos";
import { action, computed, observable, runInAction } from "mobx";
import { NOT_LOADED } from "../../Infra/RemoteData";
const RUNS_PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 3000;
const ISO_DATE = "yyyy-MM-dd";
export class GlobalAdminReportsViewModel {
    constructor(container) {
        this.pollTimer = null;
        this.definitions = NOT_LOADED;
        this.runs = NOT_LOADED;
        this.selectedReportType = null;
        this.paramFields = [];
        this.isGenerating = false;
        this.errorMessage = null;
        this.nextBefore = undefined;
        this.isLoadingMore = false;
        this.watchedRunIds = observable.set();
        this.onlySelectedReport = false;
        this.reportsAdminService = container.reportsAdminService;
        void this.load();
    }
    stop() {
        this.stopPolling();
    }
    get selectedDefinition() {
        var _a;
        if (this.definitions === NOT_LOADED || this.selectedReportType === null)
            return null;
        return (_a = this.definitions.find((d) => d.type === this.selectedReportType)) !== null && _a !== void 0 ? _a : null;
    }
    get runFilter() {
        return this.onlySelectedReport && this.selectedReportType !== null ? { reportType: this.selectedReportType } : {};
    }
    get hasMoreRuns() {
        return this.nextBefore !== undefined;
    }
    get isAnyRunActive() {
        if (this.runs === NOT_LOADED)
            return false;
        return this.runs.some((run) => run.status === ReportRunStatus.QUEUED || run.status === ReportRunStatus.RUNNING);
    }
    async load() {
        this.errorMessage = null;
        try {
            const [definitions, runList] = await Promise.all([
                this.reportsAdminService.listDefinitions(),
                this.reportsAdminService.listRuns({ limit: RUNS_PAGE_SIZE }),
            ]);
            runInAction(() => {
                this.definitions = definitions;
                this.runs = runList.runs;
                this.nextBefore = runList.nextBefore;
                if (this.selectedReportType === null && definitions.length > 0) {
                    this.selectReport(definitions[0].type);
                }
            });
            this.syncPolling();
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
    }
    setOnlySelectedReport(only) {
        this.onlySelectedReport = only;
        void this.refreshRuns();
    }
    selectReport(reportType) {
        var _a;
        this.selectedReportType = reportType;
        this.errorMessage = null;
        if (this.onlySelectedReport)
            void this.refreshRuns();
        const definition = this.definitions === NOT_LOADED ? undefined : this.definitions.find((d) => d.type === reportType);
        this.paramFields = ((_a = definition === null || definition === void 0 ? void 0 : definition.params) !== null && _a !== void 0 ? _a : []).map((param) => ({ name: param.name, type: param.type, value: "" }));
        if (this.hasDateRange)
            this.applyDateRange(startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1)));
    }
    setParamValue(name, value) {
        const field = this.paramFields.find((f) => f.name === name);
        if (field)
            field.value = value;
    }
    get hasDateRange() {
        const dates = this.paramFields.filter((f) => f.type === ReportParamType.DATE);
        return dates.length === 2 && dates.some((f) => f.name === "from") && dates.some((f) => f.name === "to");
    }
    applyDateRange(from, to) {
        this.setParamValue("from", format(from, ISO_DATE));
        this.setParamValue("to", format(to, ISO_DATE));
    }
    setLastMonth() {
        const previous = subMonths(new Date(), 1);
        this.applyDateRange(startOfMonth(previous), endOfMonth(previous));
    }
    setThisMonth() {
        const now = new Date();
        this.applyDateRange(startOfMonth(now), now);
    }
    setLast30Days() {
        const now = new Date();
        this.applyDateRange(subDays(now, 30), now);
    }
    setLast90Days() {
        const now = new Date();
        this.applyDateRange(subDays(now, 90), now);
    }
    get canGenerate() {
        if (this.selectedReportType === null || this.isGenerating)
            return false;
        return this.paramFields.every((field) => field.value.trim().length > 0);
    }
    async generate() {
        if (this.selectedReportType === null || !this.canGenerate)
            return;
        this.isGenerating = true;
        this.errorMessage = null;
        try {
            const params = Object.fromEntries(this.paramFields.map((f) => [f.name, f.value.trim()]));
            const queued = await this.reportsAdminService.runReport(this.selectedReportType, this.paramFields.length > 0 ? params : undefined);
            runInAction(() => this.watchedRunIds.add(queued.runId));
            await this.refreshRuns();
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
        finally {
            runInAction(() => {
                this.isGenerating = false;
            });
        }
    }
    async refreshRuns() {
        try {
            const runList = await this.reportsAdminService.listRuns({ limit: RUNS_PAGE_SIZE, ...this.runFilter });
            runInAction(() => {
                this.runs = runList.runs;
                this.nextBefore = runList.nextBefore;
            });
            this.syncPolling();
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
    }
    async loadMoreRuns() {
        if (this.nextBefore === undefined || this.runs === NOT_LOADED || this.isLoadingMore)
            return;
        this.isLoadingMore = true;
        try {
            const existing = this.runs;
            const runList = await this.reportsAdminService.listRuns({
                limit: RUNS_PAGE_SIZE,
                before: this.nextBefore,
                ...this.runFilter,
            });
            runInAction(() => {
                this.runs = [...existing, ...runList.runs];
                this.nextBefore = runList.nextBefore;
            });
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
        finally {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }
    async download(run) {
        this.errorMessage = null;
        try {
            const { url } = await this.reportsAdminService.getDownload(run.id);
            window.open(url, "_blank", "noopener,noreferrer");
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
    }
    syncPolling() {
        if (this.isAnyRunActive) {
            if (this.pollTimer === null) {
                this.pollTimer = setInterval(() => void this.refreshRuns(), POLL_INTERVAL_MS);
            }
        }
        else {
            this.stopPolling();
        }
    }
    stopPolling() {
        if (this.pollTimer !== null) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }
}
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "definitions", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "runs", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "selectedReportType", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "paramFields", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "isGenerating", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "errorMessage", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "nextBefore", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "isLoadingMore", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "watchedRunIds", void 0);
__decorate([
    observable
], GlobalAdminReportsViewModel.prototype, "onlySelectedReport", void 0);
__decorate([
    computed
], GlobalAdminReportsViewModel.prototype, "selectedDefinition", null);
__decorate([
    computed
], GlobalAdminReportsViewModel.prototype, "runFilter", null);
__decorate([
    computed
], GlobalAdminReportsViewModel.prototype, "hasMoreRuns", null);
__decorate([
    computed
], GlobalAdminReportsViewModel.prototype, "isAnyRunActive", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "setOnlySelectedReport", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "selectReport", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "setParamValue", null);
__decorate([
    computed
], GlobalAdminReportsViewModel.prototype, "hasDateRange", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "applyDateRange", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "setLastMonth", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "setThisMonth", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "setLast30Days", null);
__decorate([
    action
], GlobalAdminReportsViewModel.prototype, "setLast90Days", null);
__decorate([
    computed
], GlobalAdminReportsViewModel.prototype, "canGenerate", null);
function describeError(error) {
    const message = readApiMessage(error);
    if (message !== null)
        return message;
    return error instanceof Error ? error.message : "Something went wrong";
}
function readApiMessage(error) {
    if (typeof error !== "object" || error === null || !("response" in error))
        return null;
    const response = Reflect.get(error, "response");
    if (typeof response !== "object" || response === null)
        return null;
    const data = Reflect.get(response, "data");
    if (typeof data !== "object" || data === null)
        return null;
    for (const key of ["message", "error"]) {
        const value = Reflect.get(data, key);
        if (typeof value === "string" && value.length > 0)
            return value;
    }
    return null;
}
